import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, handleApiError, type Station } from "@/lib/api-client";

// Query keys factory
export const queryKeys = {
  all: ["stations"] as const,
  lists: () => [...queryKeys.all, "list"] as const,
  list: (filters: { active?: boolean }) =>
    [...queryKeys.lists(), filters] as const,
  details: () => [...queryKeys.all, "detail"] as const,
  detail: (id: string) => [...queryKeys.details(), id] as const,
};

// Hook to get a single station
export function useStation(stationId: string) {
  return useQuery({
    queryKey: queryKeys.detail(stationId),
    queryFn: () => apiClient.stations.get(stationId),
    staleTime: 1000, // Consider data stale after 1 second
    refetchInterval: 5000, // Refetch every 5 seconds for real-time updates
    retry: (failureCount, error) => {
      // Don't retry on 404s
      if (error instanceof Error && error.message.includes("404")) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// Hook to get all active stations
export function useActiveStations() {
  return useQuery({
    queryKey: queryKeys.list({ active: true }),
    queryFn: () => apiClient.stations.listActive(),
    staleTime: 1000,
    refetchInterval: 5000, // Poll for updates
  });
}

// Hook to get all stations (currently same as active stations)
export function useAllStations() {
  return useQuery({
    queryKey: queryKeys.lists(),
    queryFn: () => apiClient.stations.listActive(), // Use the same discover endpoint
    staleTime: 1000,
    refetchInterval: 5000,
  });
}

// Hook to join a station as viewer
export function useJoinStation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.stations.joinAsReceiver,
    onSuccess: (_data, variables) => {
      // Invalidate station query to get updated viewer count
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.stationId),
      });
    },
    onError: (error) => {
      handleApiError(error, "Failed to join station");
    },
  });
}

// Hook to create a station
export function useCreateStation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.stations.create,
    onSuccess: () => {
      // Invalidate all station lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
    },
    onError: (error) => {
      handleApiError(error, "Failed to create station");
    },
  });
}

// Hook to update station
export function useUpdateStation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.stations.update,
    onSuccess: (data) => {
      // Update the cache with the new data
      queryClient.setQueryData(queryKeys.detail(data._id), data);
      // Also invalidate lists in case name changed
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
    },
    onError: (error) => {
      handleApiError(error, "Failed to update station");
    },
  });
}

// Hook to update now playing
export function useUpdateNowPlaying() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: apiClient.stations.updateNowPlaying,
    onSuccess: (data) => {
      // Update the cache with the new data
      queryClient.setQueryData(queryKeys.detail(data._id), data);
    },
    onError: (error) => {
      handleApiError(error, "Failed to update now playing");
    },
  });
}

// Hook to end a station
export function useEndStation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (stationId: string) => apiClient.stations.end(stationId),
    onSuccess: (_, stationId) => {
      // Invalidate this station and all lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(stationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
    },
    onError: (error) => {
      handleApiError(error, "Failed to end station");
    },
  });
}

// Custom hook for station subscription with polling
export function useStationSubscription(
  stationId: string,
  options?: {
    enabled?: boolean;
    onStationUpdate?: (station: Station) => void;
  }
) {
  const { enabled = true, onStationUpdate } = options || {};

  return useQuery({
    queryKey: queryKeys.detail(stationId),
    queryFn: () => apiClient.stations.get(stationId),
    enabled,
    staleTime: 0, // Always consider stale for real-time feel
    refetchInterval: 2000, // More frequent updates for individual station
    refetchIntervalInBackground: true, // Keep updating in background
    onSuccess: (data) => {
      onStationUpdate?.(data);
    },
  });
}

// Helper hook to prefetch station data
export function usePrefetchStation() {
  const queryClient = useQueryClient();

  return (stationId: string) => {
    return queryClient.prefetchQuery({
      queryKey: queryKeys.detail(stationId),
      queryFn: () => apiClient.stations.get(stationId),
      staleTime: 10_000, // Cache for 10 seconds
    });
  };
}
